import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import * as XLSX from 'xlsx';
import { readExcelFile, calculateFileScore } from '../utils/excelOperations';
import { distributeDosyalar } from '../utils/distributionOperations';
import { useNavigate, useLocation } from 'react-router-dom';
import { turkishToLower } from '../utils/stringUtils';
import { shuffle } from '../utils/arrayUtils';
import FileUploader from './fileManagement/FileUploader';
import DragDropArea from './fileManagement/DragDropArea';
import ActionButtons from './fileManagement/ActionButtons';
import TabNavigation from './fileManagement/TabNavigation';
import MainTable from './fileManagement/MainTable';
import SummaryReport from './fileManagement/SummaryReport';
import JudgeDetails from './fileManagement/JudgeDetails';
import DistributionResults from './DistributionResults';
import NavigationButtons from './NavigationButtons';
import FileDetailModal from './FileDetailModal';
import JudgeEditModal from './JudgeEditModal';
import OverwriteModal from './fileManagement/OverwriteModal';
import { readOdsFile } from '../utils/odsOperations';
import {
  revertDistributionDeltaFromJudges,
  getDosyaSayisiByJudgeNameFromDistribution,
  readMapEntry,
} from '../utils/judgeScoreDelta';
import { useCalculationSettings } from './fileManagement/hooks/useCalculationSettings';
import { useJudgesSync } from './fileManagement/hooks/useJudgesSync';
import {
  useDistributionFolders,
  upsertDistributionInFolders,
} from './fileManagement/hooks/useDistributionFolders';

const getBaselineEntry = (baseline, judgeId) => {
  if (!baseline) return 0;
  const a = baseline[judgeId];
  if (a !== undefined && a !== null) return a;
  const b = baseline[String(judgeId)];
  if (b !== undefined && b !== null) return b;
  return 0;
};

/** `isAssignedToFormerJudge` ile aynı kural (modül düzeyinde tekrar kullanım için) */
const matchFormerJudgeAssignment = (file, atananHakim) => {
  if (!file.eskiHakim || !atananHakim) return false;
  const normalizedEski = turkishToLower(String(file.eskiHakim).trim());
  const normalizedAtanan = turkishToLower(String(atananHakim).trim());
  if (!normalizedEski || !normalizedAtanan) return false;
  return normalizedEski.includes(normalizedAtanan) || normalizedAtanan.includes(normalizedEski);
};

/** Genel hakim listesi (localStorage) üzerinden kayıt öncesi puanlar — deltada React ile LS farkını yakalamak için */
const readGlobalJudgeScoresBeforeSave = () => {
  try {
    const raw = localStorage.getItem('judges');
    if (!raw) return null;
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return null;
    return Object.fromEntries(list.map((j) => [String(j.id), Number(j.score) || 0]));
  } catch {
    return null;
  }
};

/** Kayıt öncesi genel hakim dosya sayıları — üzerine yazmada net dosyaSayisi farkını doğru hesaplamak için */
const readGlobalJudgeDosyaSayisiBeforeSave = () => {
  try {
    const raw = localStorage.getItem('judges');
    if (!raw) return null;
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return null;
    return Object.fromEntries(list.map((j) => [String(j.id), Number(j.dosyaSayisi) || 0]));
  } catch {
    return null;
  }
};

const recalcDengesizlikOrani = (mevcutByJudgeId, hedefMap, judgeList) => {
  if (!hedefMap || !Object.keys(hedefMap).length) return null;
  let toplamSapma = 0;
  let toplamHedef = 0;
  judgeList.forEach((j) => {
    const hedef = hedefMap[j.id] ?? hedefMap[String(j.id)] ?? 0;
    const mevcut = mevcutByJudgeId[j.id] ?? mevcutByJudgeId[String(j.id)] ?? 0;
    toplamSapma += Math.abs(mevcut - hedef);
    toplamHedef += hedef;
  });
  return toplamHedef > 0 ? toplamSapma / toplamHedef : 0;
};

const FileManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { calculationSettings, recalculateFiles } = useCalculationSettings();
  const { judges, setJudges, commitJudges } = useJudgesSync();
  const {
    folders,
    setFolders,
    activeFolder,
    setActiveFolder,
    createDefaultFolder,
  } = useDistributionFolders();

  const [files, setFiles] = useState([]);
  const [distributedFiles, setDistributedFiles] = useState([]);
  const [summaryReport, setSummaryReport] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');

  const [activeTab, setActiveTab] = useState('mainTable');

  const [selectedFile, setSelectedFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [distributionResults, setDistributionResults] = useState(null);
  const [judgeModalJudge, setJudgeModalJudge] = useState(null);
  const [judgeDetailsSortOrder, setJudgeDetailsSortOrder] = useState('alphabetical');
  /** Hakim kartlarının manuel sırası (id dizisi); null iken sıralama butonlarına göre */
  const [judgeDetailsCardOrder, setJudgeDetailsCardOrder] = useState(null);
  /** MainTable'dan "Atanan Hakim"e tıklandığında JudgeDetails'in scroll+highlight tetiklemesi için */
  const [scrollToJudgeId, setScrollToJudgeId] = useState(null);

  const handleJudgeClickFromMainTable = (judgeName) => {
    if (!judgeName) return;
    const judge = judges.find((j) => j.name === judgeName);
    if (!judge) return;
    setActiveTab('judgeDetails');
    setScrollToJudgeId(judge.id);
  };
  const [distributionStatus, setDistributionStatus] = useState('idle'); // 'idle', 'distributing', 'completed'

  /** Aynı dosya adıyla üzerine yazarken: seçenek modalı */
  const [overwriteModal, setOverwriteModal] = useState(null);

  /** Dağıtım algoritması ile aynı: tevzi öncesi hakim puanı (manuel taşımada baz + yük korunur) */
  const judgeScoreBaselineBeforeDistributionRef = useRef(null);
  /** Hakim detay kartlarında mevcut / tevzi sonrası dosya sayısı için tevzi öncesi genel dosya adedi */
  const judgeDosyaSayisiBaselineBeforeDistributionRef = useRef(null);
  /**
   * O an ekranda açık olan tevzi hangi kayıtla eşleşiyor? Bu bilinmiyorsa (yeni bir
   * excel yüklendi ve henüz kaydedilmedi) null. Dolu ise manuel taşımalar anında
   * kaydedilmiş tevziye yansıtılır ve (applied=true ise) hakim listesi
   * localStorage'da da güncellenir.
   */
  const loadedDistributionRef = useRef(null);
  /**
   * "Tüm Sonuçları Göster" ile açılan birleşik görünüm: klasördeki tüm tevzilerin
   * dosyaları birleştirilmiştir. Dağıt'a basılırsa: klasördeki tüm tevziler revert
   * edilir, yeniden dağıtım yapılır ve sonuç TEK YENİ kayıt olarak yazılır.
   * Diğer akışlarda (yeni dosya, tek tevzi yükleme) null'a çekilir.
   */
  const allResultsClusterRef = useRef(null);

  const isFileUploaded = files.length > 0;
  const isDistributionCompleted = distributedFiles.length > 0;

  useEffect(() => {
    const state = location.state;
    if (!state) return;

    if (state.loadDistribution) {
      loadSavedDistribution(state.folderId, state.distributionIndex);
      navigate(location.pathname, { replace: true, state: null });
      return;
    }

    if (state.loadAllResults) {
      setFiles(state.allFiles);
      setDistributedFiles(state.allDistributedFiles);
      setSummaryReport(state.combinedSummary);
      setLastDistributionDate(new Date());
      setDistributionResults(null);

      // "Tüm Sonuçları Göster" akışı: klasörün birleşik görünümü açıldı.
      // Dağıt'a basılırsa: klasördeki tüm tevzileri revert edip yeniden dağıt
      // ve sonucu TEK YENİ kayıt olarak yaz (handleDistribute içinde).
      const folder = folders.find((f) => f.id === state.folderId);
      if (folder) {
        setUploadedFileName(`${folder.name} (Birleşik)`);
      }
      allResultsClusterRef.current = { folderId: state.folderId };
      loadedDistributionRef.current = null;
      judgeScoreBaselineBeforeDistributionRef.current = null;
      judgeDosyaSayisiBaselineBeforeDistributionRef.current = null;

      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigate]);

  const loadSavedDistribution = (folderId, distributionIndex) => {
    const foldersString = localStorage.getItem('distributionFolders');
    if (!foldersString) return;

    const foldersFromStorage = JSON.parse(foldersString);
    const folder = foldersFromStorage.find((f) => f.id === folderId);
    const distribution = folder?.distributions?.[distributionIndex];
    if (!distribution) return;

    const judgesList = JSON.parse(localStorage.getItem('judges') || '[]');
    // Kayıt genel listeye uygulandıysa güncel `judge.score` bu tevziyi zaten içerir;
    // uygulanmadıysa (manuel kaydetme) içermez. Buna göre "Önce/Sonra" ve dosya
    // sayılarını kayıt anındaki değerler yerine GÜNCEL hakim durumundan türetiyoruz.
    const appliedNow = distribution.scoresAppliedToJudges !== false;

    const rebuiltYeniMap = {};
    const rebuiltBaseline = {};
    const rebuiltDosyaBaseline = {};

    judgesList.forEach((judge) => {
      const dosyalar = (distribution.distributedFiles || []).filter(
        (f) => f.atananHakim === judge.name
      );
      const loadSum = dosyalar.reduce((sum, f) => {
        if (matchFormerJudgeAssignment(f, judge.name)) {
          return sum + f.dosyaPuani * calculationSettings.hakimKendiDosyasiCarpani;
        }
        return sum + f.dosyaPuani;
      }, 0);

      const curScore = Number(judge.score) || 0;
      const curDosyaCount = Number(judge.dosyaSayisi) || 0;
      const thisDosyaCount = dosyalar.length;

      const tevziSonrasi = appliedNow ? curScore : curScore + loadSum;
      const tevziOncesi = tevziSonrasi - loadSum;
      const oncekiDosyaSayisi = appliedNow
        ? Math.max(0, curDosyaCount - thisDosyaCount)
        : curDosyaCount;

      rebuiltYeniMap[String(judge.id)] = tevziSonrasi;
      rebuiltBaseline[String(judge.id)] = tevziOncesi;
      rebuiltDosyaBaseline[String(judge.id)] = oncekiDosyaSayisi;
    });

    setActiveFolder(folderId);
    setFolders(foldersFromStorage);
    setFiles(distribution.files);
    setDistributedFiles(distribution.distributedFiles);
    setSummaryReport({
      ...(distribution.summaryReport || {}),
      yeniHakimPuanlari: rebuiltYeniMap,
    });
    setDistributionResults(null);
    setLastDistributionDate(new Date(distribution.lastDistributionDate));
    setUploadedFileName(distribution.fileName);

    judgeScoreBaselineBeforeDistributionRef.current = rebuiltBaseline;
    judgeDosyaSayisiBaselineBeforeDistributionRef.current = rebuiltDosyaBaseline;
    loadedDistributionRef.current = {
      folderId,
      distributionIndex,
      applied: appliedNow,
      fileName: distribution.fileName,
    };
    allResultsClusterRef.current = null;
  };

  const handleFileUpload = async (uploadedFile) => {
    try {
      setUploadProgress(0);
      setUploadedFileName(uploadedFile.name);
      const columnMappings = JSON.parse(localStorage.getItem('columnSettings'))?.columnMappings || {};
      
      let data;
      if (uploadedFile.name.endsWith('.ods')) {
        data = await readOdsFile(uploadedFile, (progress) => {
          setUploadProgress(progress);
        }, columnMappings);
      } else {
        data = await readExcelFile(uploadedFile, (progress) => {
          setUploadProgress(progress);
        }, columnMappings);
      }
      
      const calculatedData = data.map(file => calculateFileScore(file, calculationSettings));
      
      setFiles(calculatedData);
      setDistributedFiles([]);
      setSummaryReport(null);
      setDistributionResults(null);
      judgeScoreBaselineBeforeDistributionRef.current = null;
      judgeDosyaSayisiBaselineBeforeDistributionRef.current = null;
      loadedDistributionRef.current = null;
      allResultsClusterRef.current = null;

      setActiveTab('mainTable');
    } catch (error) {
      console.error('Dosya okuma hatası:', error.message);
      alert('Dosya yüklenirken bir hata oluştu: ' + error.message);
    } finally {
      setUploadProgress(0);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file && (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                 file.type === 'application/vnd.ms-excel' ||
                 file.type === 'application/vnd.oasis.opendocument.spreadsheet' ||
                 file.name.endsWith('.ods'))) {
      handleFileUpload(file);
    } else {
      alert('Lütfen sadece Excel veya ODS dosyası yükleyin.');
    }
  };

  const [lastDistributionDate, setLastDistributionDate] = useState(null);

  const handleDistribute = async () => {
    if (files.length === 0 || judges.length === 0) {
      console.warn('Dağıtım için dosya veya hakim bulunmuyor.');
      return;
    }

    setDistributionStatus('distributing');

    // Kayıtlı bir tevzi açıksa ve genel hakim listesine uygulanmışsa, o kaydın
    // etkisini judges'tan geri al (revert). Aksi halde aynı tevzi yeniden
    // dağıtıldığında eski yük + yeni yük çakışır → mükerrer puan.
    const loaded = loadedDistributionRef.current;
    const cluster = allResultsClusterRef.current;
    let effectiveJudges = judges;
    let isRedistributingSaved = false;

    if (loaded && loaded.applied) {
      const folder = folders.find((f) => f.id === loaded.folderId);
      const oldDist = folder?.distributions?.[loaded.distributionIndex];
      if (oldDist) {
        effectiveJudges = revertDistributionDeltaFromJudges(judges, oldDist);
        isRedistributingSaved = true;
      }
    } else if (cluster) {
      // "Tüm Sonuçları Göster" sonrası dağıtım: klasördeki tüm tevzilerin
      // etkisini sırayla geri al; cluster save akışı handleSaveResults içinde yapılır.
      const folder = folders.find((f) => f.id === cluster.folderId);
      if (folder && Array.isArray(folder.distributions) && folder.distributions.length > 0) {
        let reverted = judges;
        for (const dist of folder.distributions) {
          if (dist.scoresAppliedToJudges !== false) {
            reverted = revertDistributionDeltaFromJudges(reverted, dist);
          }
        }
        effectiveJudges = reverted;
      }
    }

    judgeScoreBaselineBeforeDistributionRef.current = Object.fromEntries(
      effectiveJudges.map((j) => [String(j.id), j.score ?? 0])
    );
    judgeDosyaSayisiBaselineBeforeDistributionRef.current = Object.fromEntries(
      effectiveJudges.map((j) => [String(j.id), Number(j.dosyaSayisi) || 0])
    );

    if (!isRedistributingSaved) {
      loadedDistributionRef.current = null;
    }

    // Hakim listesini karıştır (revert edilmiş veya orijinal judges üzerinden)
    const shuffledJudges = shuffle([...effectiveJudges]);

    try {
      const results = await distributeDosyalar(files, shuffledJudges, calculationSettings);
      setDistributionResults(results);

      const { updatedFiles, dagitilmisDosyalar, newSummary } =
        buildStateFromDistributionResults(results, effectiveJudges, files);
      setFiles(updatedFiles);
      setDistributedFiles(dagitilmisDosyalar);
      setSummaryReport(newSummary);

      const savedAt = new Date();
      setLastDistributionDate(savedAt);

      // Otomatik kayıt YOK — kullanıcı "Sonuçları Kaydet"e basana kadar
      // judges fiziksel olarak değişmez, kayıtlar dokunulmaz. handleSaveResults
      // cluster veya loaded mod'una göre doğru save akışını seçer.
      // (effectiveJudges revert, isRedistributingSaved, isRedistributingCluster,
      // savedFolderId, savedDistIndex, savedFileName, clusterFolderId değişkenleri
      // sadece baseline hesabını doğru kurmak için kullanıldı.)

      if (results.toplamDosyaSayisi !== results.dagitilmisDosyaSayisi) {
        console.warn(
          `Uyarı: Toplam dosya sayısı (${results.toplamDosyaSayisi}) ile dağıtılan dosya sayısı (${results.dagitilmisDosyaSayisi}) eşleşmiyor.`
        );
      }

      setDistributionStatus('completed');
      setTimeout(() => setDistributionStatus('idle'), 750);
    } catch (error) {
      console.error('Dağıtım sırasında bir hata oluştu:', error);
      setDistributionStatus('idle');
    }
  };

  const buildDistributionPayload = (options = {}) => {
    const {
      judgesSnapshot = judges,
      summarySnapshot = summaryReport,
      filesSnapshot = files,
      distributedSnapshot = distributedFiles,
      lastDate = lastDistributionDate,
      scoresAppliedToJudges = true,
      judgeGlobalScoresBeforeSave = null,
      judgeGlobalDosyaSayisiBeforeSave = null,
    } = options;

    const hasGlobal =
      judgeGlobalScoresBeforeSave &&
      typeof judgeGlobalScoresBeforeSave === 'object' &&
      Object.keys(judgeGlobalScoresBeforeSave).length > 0;

    const hasGlobalDosya =
      judgeGlobalDosyaSayisiBeforeSave &&
      typeof judgeGlobalDosyaSayisiBeforeSave === 'object' &&
      Object.keys(judgeGlobalDosyaSayisiBeforeSave).length > 0;

    const judgeScoresBeforeSave = Object.fromEntries(
      judgesSnapshot.map((j) => {
        const id = String(j.id);
        if (hasGlobal) {
          const g = readMapEntry(judgeGlobalScoresBeforeSave, j.id);
          const fromDisk = g !== undefined && g !== null ? Number(g) : NaN;
          if (Number.isFinite(fromDisk)) {
            return [id, fromDisk];
          }
        }
        return [id, Number(j.score) || 0];
      })
    );

    const dosyaByName = getDosyaSayisiByJudgeNameFromDistribution({
      summaryReport: summarySnapshot,
      distributedFiles: distributedSnapshot,
    });

    // Bu tevzinin hakimlere kattığı TAM puan katkısı:
    // hakime atanan dosyaların alinanPuan toplamı. Üzerine yazmada yanıltıcı
    // "net kayıt farkı" yerine bu kullanılır — "Geri al" bu excelin tüm etkisini
    // kaldırsın ve ilk kayıt ile sonraki kayıt arasında mükerrer puanlama oluşmasın.
    const judgeScoreDeltaFromSave = Object.fromEntries(
      judgesSnapshot.map((j) => {
        const loadSum = (filesSnapshot || []).reduce((sum, f) => {
          if (!f || f.atananHakim !== j.name) return sum;
          const puan = Number(f.alinanPuan);
          return sum + (Number.isFinite(puan) ? puan : 0);
        }, 0);
        return [String(j.id), loadSum];
      })
    );

    // Bu tevzinin hakimlere kattığı TAM dosya sayısı katkısı.
    const judgeDosyaSayisiDeltaFromSave = Object.fromEntries(
      judgesSnapshot.map((j) => {
        const cnt = dosyaByName.get(j.name);
        return [String(j.id), Number.isFinite(cnt) ? Number(cnt) : 0];
      })
    );

    const judgeDosyaSayisiBeforeSave = Object.fromEntries(
      judgesSnapshot.map((j) => {
        const id = String(j.id);
        if (hasGlobalDosya) {
          const g = readMapEntry(judgeGlobalDosyaSayisiBeforeSave, j.id);
          const fromDisk = g !== undefined && g !== null ? Number(g) : NaN;
          if (Number.isFinite(fromDisk)) {
            return [id, fromDisk];
          }
        }
        return [id, Number(j.dosyaSayisi) || 0];
      })
    );

    return {
      files: filesSnapshot,
      distributedFiles: distributedSnapshot,
      summaryReport: summarySnapshot,
      lastDistributionDate: lastDate.toISOString(),
      fileName: uploadedFileName,
      judgeScoresBeforeSave,
      judgeScoreDeltaFromSave,
      judgeDosyaSayisiBeforeSave,
      judgeDosyaSayisiDeltaFromSave,
      scoresAppliedToJudges,
    };
  };

  const persistDistributionAndMaybeUpdateJudges = (
    folderId,
    existingIndex,
    distributionToSave,
    applyScoresToJudges
  ) => {
    setFolders((prev) => {
      const updated = upsertDistributionInFolders(prev, folderId, existingIndex, distributionToSave);
      localStorage.setItem('distributionFolders', JSON.stringify(updated));
      return updated;
    });

    if (applyScoresToJudges) {
      const yeniMap = distributionToSave.summaryReport?.yeniHakimPuanlari || {};
      const dosyaByName = getDosyaSayisiByJudgeNameFromDistribution(distributionToSave);
      const updatedJudges = judges.map((judge) => {
        const ekDosya = dosyaByName.get(judge.name) ?? 0;
        const curCount = Number(judge.dosyaSayisi) || 0;
        return {
          ...judge,
          score: yeniMap[judge.id] ?? yeniMap[String(judge.id)] ?? judge.score,
          dosyaSayisi: curCount + (Number.isFinite(ekDosya) ? ekDosya : 0),
        };
      });
      commitJudges(updatedJudges);
    }
  };

  const handleSaveResults = () => {
    if (!summaryReport || !lastDistributionDate) {
      alert('Önce dosya dağıtımı yapmalısınız.');
      return;
    }

    // "Tüm Sonuçları Göster" sonrası kaydetme: klasördeki tüm tevziler revert
    // edilir, mevcut atamalara göre puanlar yeniden hesaplanır ve sonuç TEK YENİ
    // kayıt olarak yazılır (eski tevziler silinir).
    const cluster = allResultsClusterRef.current;
    if (cluster) {
      const folder = folders.find((f) => f.id === cluster.folderId);
      if (!folder) {
        alert('Birleşik dağıtımın kaynağı klasör bulunamadı.');
        return;
      }

      let revertedJudges = judges;
      for (const dist of folder.distributions) {
        if (dist.scoresAppliedToJudges !== false) {
          revertedJudges = revertDistributionDeltaFromJudges(revertedJudges, dist);
        }
      }

      const dosyaByName = getDosyaSayisiByJudgeNameFromDistribution({
        summaryReport,
        distributedFiles,
      });

      const judgeScores = {};
      revertedJudges.forEach((judge) => {
        const assigned = distributedFiles.filter((f) => f.atananHakim === judge.name);
        const loadSum = assigned.reduce((sum, f) => {
          if (isAssignedToFormerJudge(f)) {
            return sum + f.dosyaPuani * calculationSettings.hakimKendiDosyasiCarpani;
          }
          return sum + f.dosyaPuani;
        }, 0);
        judgeScores[judge.id] = (Number(judge.score) || 0) + loadSum;
      });

      const yeniHakimPuanlari = Object.fromEntries(
        revertedJudges.map((j) => [String(j.id), judgeScores[j.id] ?? j.score ?? 0])
      );
      const newSummary = { ...summaryReport, yeniHakimPuanlari };

      const mergedJudges = revertedJudges.map((judge) => {
        const ekDosya = dosyaByName.get(judge.name) ?? 0;
        const curCount = Number(judge.dosyaSayisi) || 0;
        return {
          ...judge,
          score: judgeScores[judge.id] ?? judge.score,
          dosyaSayisi: curCount + (Number.isFinite(ekDosya) ? ekDosya : 0),
        };
      });

      const distributionToSave = buildDistributionPayload({
        judgesSnapshot: revertedJudges,
        summarySnapshot: newSummary,
        scoresAppliedToJudges: true,
      });

      setFolders((prev) => {
        const updated = prev.map((f) =>
          f.id === cluster.folderId ? { ...f, distributions: [distributionToSave] } : f
        );
        localStorage.setItem('distributionFolders', JSON.stringify(updated));
        return updated;
      });
      commitJudges(mergedJudges);
      setSummaryReport(newSummary);

      loadedDistributionRef.current = {
        folderId: cluster.folderId,
        distributionIndex: 0,
        applied: true,
        fileName: distributionToSave.fileName,
      };
      allResultsClusterRef.current = null;

      alert('Birleşik dağıtım kaydedildi. Klasördeki tüm eski tevziler silindi ve tek bir kayıt olarak yazıldı.');
      return;
    }

    let currentActiveFolder = activeFolder;
    let currentFolders = folders;

    if (!currentActiveFolder) {
      const newFolder = createDefaultFolder();
      currentActiveFolder = newFolder.id;
      currentFolders = [newFolder];
    }

    const activeFolderObject = currentFolders.find((folder) => folder.id === currentActiveFolder);

    if (!activeFolderObject) {
      alert('Aktif klasör bulunamadı. Lütfen bir klasör seçin veya oluşturun.');
      return;
    }

    const existingDistributionIndex = activeFolderObject.distributions.findIndex(
      (dist) => dist.fileName === uploadedFileName
    );

    if (existingDistributionIndex !== -1) {
      setOverwriteModal({
        existingIndex: existingDistributionIndex,
        oldDistribution: activeFolderObject.distributions[existingDistributionIndex],
      });
      return;
    }

    const distributionToSave = buildDistributionPayload({
      scoresAppliedToJudges: true,
      judgeGlobalScoresBeforeSave: readGlobalJudgeScoresBeforeSave() ?? undefined,
      judgeGlobalDosyaSayisiBeforeSave: readGlobalJudgeDosyaSayisiBeforeSave() ?? undefined,
    });
    const newIndex = activeFolderObject.distributions.length;
    persistDistributionAndMaybeUpdateJudges(currentActiveFolder, -1, distributionToSave, true);
    loadedDistributionRef.current = {
      folderId: currentActiveFolder,
      distributionIndex: newIndex,
      applied: true,
      fileName: distributionToSave.fileName,
    };
    alert('Sonuçlar başarıyla kaydedildi ve hakim puanları güncellendi.');
  };

  // "Tüm Sonuçları Göster" görünümünde aktif olur. Eski klasörü ve hakim puanlarını
  // KORUR; birleşik dağıtımı YENİ bir klasöre tek kayıt olarak yazar. Yeni kayıt
  // `scoresAppliedToJudges: false` ile yazılır → judges'a mükerrer puan eklenmez
  // (çünkü orijinal kayıtların etkisi judges'ta zaten var).
  const handleSaveAsNewFolder = () => {
    if (!summaryReport || !lastDistributionDate) {
      alert('Önce dosya dağıtımı yapmalısınız.');
      return;
    }
    const cluster = allResultsClusterRef.current;
    if (!cluster) {
      alert('Bu işlem yalnızca birleşik (Tüm Sonuçları Göster) görünümünde kullanılabilir.');
      return;
    }
    const sourceFolder = folders.find((f) => f.id === cluster.folderId);
    const sourceName = sourceFolder?.name || 'Birleşik';
    const tarihStr = new Date().toLocaleDateString('tr-TR');
    const yeniKlasorAdi = `${sourceName} (Birleşik) — ${tarihStr}`;

    const distributionToSave = buildDistributionPayload({
      scoresAppliedToJudges: false,
    });

    const yeniKlasor = {
      id: Date.now(),
      name: yeniKlasorAdi,
      distributions: [distributionToSave],
    };

    const yeniFolders = [...folders, yeniKlasor];
    setFolders(yeniFolders);
    localStorage.setItem('distributionFolders', JSON.stringify(yeniFolders));

    // Yeni kayda işaret et, cluster mode'dan çık. applied=false: ileride bu kayıt
    // silinirse judges'tan bir şey düşülmez (zaten yansıtılmadı).
    loadedDistributionRef.current = {
      folderId: yeniKlasor.id,
      distributionIndex: 0,
      applied: false,
      fileName: distributionToSave.fileName,
    };
    allResultsClusterRef.current = null;
    setActiveFolder(yeniKlasor.id);

    alert(
      `Birleşik dağıtım "${yeniKlasorAdi}" adlı yeni klasöre kaydedildi.\n\nEski klasör ve hakim puanları korundu.`
    );
  };

  const handleOverwriteSaveRecordOnly = () => {
    if (!overwriteModal || !summaryReport || !lastDistributionDate || activeFolder == null) return;

    const distributionToSave = buildDistributionPayload({
      scoresAppliedToJudges: false,
      judgeGlobalScoresBeforeSave: readGlobalJudgeScoresBeforeSave() ?? undefined,
      judgeGlobalDosyaSayisiBeforeSave: readGlobalJudgeDosyaSayisiBeforeSave() ?? undefined,
    });
    persistDistributionAndMaybeUpdateJudges(activeFolder, overwriteModal.existingIndex, distributionToSave, false);
    loadedDistributionRef.current = {
      folderId: activeFolder,
      distributionIndex: overwriteModal.existingIndex,
      applied: false,
      fileName: distributionToSave.fileName,
    };
    setOverwriteModal(null);
    alert(
      'Tevzi kaydı güncellendi. Genel hakim puan listesi değiştirilmedi; kayıttaki dağıtım özeti ekrandaki yeni tevziyi yansıtır.'
    );
  };

  /**
   * Eski kayıt genel listeye işlendiyse etkisini geri alır; ekrandaki dağıtım (manuel taşımalar dahil) aynı kalır,
   * puanlar bu atamalara göre yeniden hesaplanır ve kayıt + hakim listesi güncellenir (mükerrer puanlamayı önler).
   */
  const handleOverwriteFullRedistribute = () => {
    if (!overwriteModal || !summaryReport || activeFolder == null) return;

    const { oldDistribution, existingIndex } = overwriteModal;
    setOverwriteModal(null);

    if (files.length === 0 || judges.length === 0) {
      alert('Dosya veya hakim bulunamadı.');
      return;
    }

    if (!distributedFiles.length) {
      alert('Dağıtılmış dosya listesi boş; işlem yapılamadı.');
      return;
    }

    const judgeGlobalScoresBeforeSave = readGlobalJudgeScoresBeforeSave() ?? undefined;
    const judgeGlobalDosyaSayisiBeforeSave = readGlobalJudgeDosyaSayisiBeforeSave() ?? undefined;

    const judgesRev = revertDistributionDeltaFromJudges(judges, oldDistribution);

    judgeScoreBaselineBeforeDistributionRef.current = Object.fromEntries(
      judgesRev.map((j) => [String(j.id), j.score ?? 0])
    );
    judgeDosyaSayisiBaselineBeforeDistributionRef.current = Object.fromEntries(
      judgesRev.map((j) => [String(j.id), Number(j.dosyaSayisi) || 0])
    );

    const baseline = judgeScoreBaselineBeforeDistributionRef.current;
    const nextDistributed = distributedFiles;

    const judgeScores = {};
    judgesRev.forEach((judge) => {
      const assigned = nextDistributed.filter((f) => f.atananHakim === judge.name);
      const loadSum = assigned.reduce((sum, f) => {
        if (isAssignedToFormerJudge(f)) {
          return sum + f.dosyaPuani * calculationSettings.hakimKendiDosyasiCarpani;
        }
        return sum + f.dosyaPuani;
      }, 0);
      const b = getBaselineEntry(baseline, judge.id);
      judgeScores[judge.id] = b + loadSum;
    });

    const yeniHakimPuanlari = Object.fromEntries(
      judgesRev.map((j) => [String(j.id), judgeScores[j.id] ?? j.score ?? 0])
    );

    const updatedFiles = files.map((f) => {
      const dist = nextDistributed.find((d) => d.id === f.id);
      if (!dist) return f;
      return {
        ...f,
        atananHakim: dist.atananHakim,
        alinanPuan: calculateAlinanPuan(f, dist.atananHakim, calculationSettings),
      };
    });

    const hedefMap =
      distributionResults?.hakimHedefPuanlari ?? summaryReport?.hakimHedefPuanlari ?? {};
    const toplamDosyaPuani = files.reduce((s, f) => s + (f.dosyaPuani || 0), 0);

    const newSummary = {
      hakimRaporlari: judgesRev.map((judge) => ({
        hakimAdi: judge.name,
        dosyaSayisi: nextDistributed.filter((f) => f.atananHakim === judge.name).length,
        toplamPuan: judgeScores[judge.id] ?? judge.score,
        hedefPuan: hedefMap[judge.id] ?? hedefMap[String(judge.id)] ?? 0,
      })),
      yeniHakimPuanlari,
      hakimHedefPuanlari: hedefMap,
      toplamDosyaPuani,
      ortalamaHakimPuani: summaryReport?.ortalamaHakimPuani ?? distributionResults?.ortalamaHakimPuani ?? 0,
      toplamDosyaSayisi: files.length,
      dagitilmisDosyaSayisi: nextDistributed.length,
    };

    const dosyaByName = getDosyaSayisiByJudgeNameFromDistribution({
      summaryReport: newSummary,
      distributedFiles: nextDistributed,
    });
    const mergedJudges = judgesRev.map((judge) => {
      const ekDosya = dosyaByName.get(judge.name) ?? 0;
      const curCount = Number(judge.dosyaSayisi) || 0;
      return {
        ...judge,
        score: judgeScores[judge.id] ?? judge.score,
        dosyaSayisi: curCount + (Number.isFinite(ekDosya) ? ekDosya : 0),
      };
    });

    const hakimDosyaSayilari = Object.fromEntries(
      judgesRev.map((j) => [String(j.id), nextDistributed.filter((f) => f.atananHakim === j.name).length])
    );
    const dengesizlikOrani = recalcDengesizlikOrani(yeniHakimPuanlari, hedefMap, judgesRev);

    setFiles(updatedFiles);
    setDistributedFiles(nextDistributed);
    setSummaryReport(newSummary);
    setDistributionResults((prev) => ({
      ...(prev || {}),
      yeniHakimPuanlari,
      hakimHedefPuanlari: hedefMap,
      hakimDosyaSayilari,
      ...(dengesizlikOrani != null ? { dengesizlikOrani } : {}),
    }));

    const savedAt = new Date();
    setLastDistributionDate(savedAt);

    const distributionToSave = buildDistributionPayload({
      judgesSnapshot: judgesRev,
      summarySnapshot: newSummary,
      filesSnapshot: updatedFiles,
      distributedSnapshot: nextDistributed,
      lastDate: savedAt,
      scoresAppliedToJudges: true,
      judgeGlobalScoresBeforeSave,
      judgeGlobalDosyaSayisiBeforeSave,
    });

    persistDistributionAndMaybeUpdateJudges(activeFolder, existingIndex, distributionToSave, false);

    commitJudges(mergedJudges);

    loadedDistributionRef.current = {
      folderId: activeFolder,
      distributionIndex: existingIndex,
      applied: true,
      fileName: distributionToSave.fileName,
    };

    alert(
      'Eski kaydın hakim puan ve dosya sayısı etkisi geri alındı; mevcut atamalarınız korunarak puanlar yeniden hesaplandı ve kayıt ile genel hakim listesi güncellendi.'
    );
  };

  const exportToExcel = () => {
    if (files.length === 0) {
      alert('Dışa aktarılacak dosya bulunmamaktadır.');
      return;
    }
  
    const workbook = XLSX.utils.book_new();
  
    // Ana sayfa tablosu
    const mainTableData = files.map((file, index) => ({
      'Sıra No': index + 1,
      'Esas No': file.esasNo,
      'Sanık S.': file.sanikSayisi,
      'Mağdur S.': file.magdurSayisi,
      'Klasör': file.klasorSayisi,
      'T. Görüşü': file.gorus,
      'SUÇ TÜRÜ': file.sucTuru,
      'Eski Hakim': file.eskiHakim || '-',
      'Eski Hakimine Tevzi Edildi mi?': file.eskiHakim ? 
        (isAssignedToFormerJudge(file) ? 'Evet' : 'Hayır') 
        : '-',
      'Manuel Tevzi Edilecek Hakim': file.manuelTevziEdilecekHakim || '-',
      'Manuel Tevzi': file.manuelTevziEdilecekHakim ? 
        (isAssignedToManualJudge(file) ? 'Evet' : 'Hayır') 
        : '-',
      'Dosya Puanı': file.dosyaPuani,
      'Dosyadan Alınan Puan': file.alinanPuan || '-',
      'Tevzi Edilen Hakim': file.atananHakim || '-'
    }));
    const mainSheet = XLSX.utils.json_to_sheet(mainTableData);
    XLSX.utils.book_append_sheet(workbook, mainSheet, "Ana Tablo");
  
    // Dağıtım özet raporu
    if (summaryReport) {
      const summaryData = summaryReport.hakimRaporlari.map(hakim => ({
        'Hakim Adı': hakim.hakimAdi,
        'Dosya Sayısı': hakim.dosyaSayisi,
        'Atanmış Dosyaların Toplam Puanı': hakim.atananDosyalarToplamPuani ? hakim.atananDosyalarToplamPuani.toFixed(2) : '0.00',
        'Eski Dosya Sayısı': hakim.eskiDosyaSayisi,
        'Eski Dosya Puanı': hakim.eskiDosyaPuani ? hakim.eskiDosyaPuani.toFixed(2) : '0.00'
      }));
      summaryData.push({
        'Hakim Adı': 'TOPLAM',
        'Dosya Sayısı': summaryReport.toplamDosyaSayisi,
        'Atanmış Dosyaların Toplam Puanı': summaryReport.toplamAtananDosyaPuani ? summaryReport.toplamAtananDosyaPuani.toFixed(2) : '0.00',
        'Eski Dosya Sayısı': summaryReport.hakimRaporlari.reduce((total, hakim) => total + (hakim.eskiDosyaSayisi || 0), 0),
        'Eski Dosya Puanı': summaryReport.hakimRaporlari.reduce((total, hakim) => total + (hakim.eskiDosyaPuani || 0), 0).toFixed(2)
      });
      const summarySheet = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, "Dağıtım Özeti");
    }
  
    // Hakimlere göre atanmış dosyalar raporu
    if (judges && distributedFiles) {
      const judgeFilesData = judges.flatMap(judge => 
        distributedFiles.filter(file => file.atananHakim === judge.name)
          .map(file => ({
            'Hakim': judge.name,
            'Esas No': file.esasNo,
            'Suç Türü': file.sucTuru,
            'Dosya Puanı': file.dosyaPuani,
            'Eski Dosya mı?': isAssignedToFormerJudge(file) ? 'Evet' : 'Hayır'
          }))
      );
      const judgeFilesSheet = XLSX.utils.json_to_sheet(judgeFilesData);
      XLSX.utils.book_append_sheet(workbook, judgeFilesSheet, "Hakim Dosyaları");
    }
  
    // Hakim detayları raporu
    if (judges && distributedFiles) {
      const judgeDetailsData = judges.map(judge => {
        const judgeDosyalar = distributedFiles.filter(file => file.atananHakim === judge.name);
        const eskiDosyalar = judgeDosyalar.filter(dosya => isAssignedToFormerJudge(dosya));
        return {
          'Hakim Adı': judge.name,
          'Toplam Dosya Sayısı': judgeDosyalar.length,
          'Toplam Puan': judgeDosyalar.reduce((sum, dosya) => sum + (dosya.dosyaPuani || 0), 0).toFixed(2),
          'Eski Dosya Sayısı': eskiDosyalar.length,
          'Eski Dosya Puanı': eskiDosyalar.reduce((sum, dosya) => sum + (dosya.dosyaPuani || 0), 0).toFixed(2)
        };
      });
      const judgeDetailsSheet = XLSX.utils.json_to_sheet(judgeDetailsData);
      XLSX.utils.book_append_sheet(workbook, judgeDetailsSheet, "Hakim Detayları");
    }
  
    // Grafik verilerini aktarma
    if (distributionResults) {
      const graphData = [
        ['Hakim', 'Hedef Puan', 'Alınan Puan'],
        ...Object.entries(distributionResults.hakimHedefPuanlari).map(([hakimId, hedefPuan]) => {
          const hakim = judges.find(j => j.id === hakimId);
          const alinanPuan = distributionResults.yeniHakimPuanlari[hakimId] || 0;
          return [hakim ? hakim.name : hakimId, hedefPuan, alinanPuan];
        })
      ];

      const graphSheet = XLSX.utils.aoa_to_sheet(graphData);
      XLSX.utils.book_append_sheet(workbook, graphSheet, "Grafik Verileri");

      // Grafik oluşturma talimatları
      const instructionsData = [
        ['Grafik Oluşturma Talimatları:'],
        ['1. "Grafik Verileri" sayfasındaki verileri seçin'],
        ['2. "Ekle" sekmesine gidin ve "Sütun Grafik" seçeneğini tıklayın'],
        ['3. "2-B Sütun" grafiğini seçin'],
        ['4. Grafik başlığını "Hakim Puan Dağılımı" olarak değiştirin'],
        ['5. X ekseni başlığını "Hakimler" olarak ayarlayın'],
        ['6. Y ekseni başlığını "Puan" olarak ayarlayın'],
        ['7. Grafik tasarımını ve renklerini tercihinize göre özelleştirin']
      ];

      const instructionsSheet = XLSX.utils.aoa_to_sheet(instructionsData);
      XLSX.utils.book_append_sheet(workbook, instructionsSheet, "Grafik Talimatları");
    }

    XLSX.writeFile(workbook, "dosya_dagitim_raporu.xlsx");
  };

  // Word raporu: hakim bazında atanan dosyalar (virgülle ayrılmış esasNo listesi)
  const exportToWord = async () => {
    if (!isDistributionCompleted) {
      alert('Önce dosya dağıtımı yapmalısınız.');
      return;
    }
    try {
      const { generateDistributionWord, downloadWordBlob } = await import('../utils/distributionWordExport');
      const blob = await generateDistributionWord({
        judges,
        distributedFiles,
        sourceFileName: uploadedFileName,
      });
      const dateStr = new Date().toISOString().slice(0, 10);
      downloadWordBlob(blob, `tevzi-rapor-${dateStr}.docx`);
    } catch (err) {
      console.error('Word dışa aktarma hatası:', err);
      alert('Word dosyası oluşturulurken bir hata oluştu: ' + (err?.message || String(err)));
    }
  };

  // Alınan puanı hesaplayan yardımcı fonksiyon
  const calculateAlinanPuan = (file, atananHakim, settings) => {
    let puan = file.dosyaPuani;
    
    // Eğer dosya eski hakime atandıysa
    if (isAssignedToFormerJudge({...file, atananHakim})) {
      puan *= settings.hakimKendiDosyasiCarpani;
    }
    // Manuel tevzi durumunda tam puan alınır, burada ek bir işlem yapmaya gerek yok
    
    return puan;
  };

  const buildStateFromDistributionResults = (results, judgesForNames, filesSource) => {
    const {
      dagitilmisDosyalar,
      yeniHakimPuanlari,
      hakimHedefPuanlari,
      toplamDosyaPuani,
      ortalamaHakimPuani,
      toplamDosyaSayisi,
      dagitilmisDosyaSayisi,
    } = results;

    const updatedFiles = filesSource.map((file) => {
      const dagitilmisDosya = dagitilmisDosyalar.find((d) => d.id === file.id);
      if (dagitilmisDosya) {
        const alinanPuan = calculateAlinanPuan(file, dagitilmisDosya.atananHakim, calculationSettings);
        return { ...file, atananHakim: dagitilmisDosya.atananHakim, alinanPuan };
      }
      return file;
    });

    const newSummary = {
      hakimRaporlari: judgesForNames.map((judge) => ({
        hakimAdi: judge.name,
        dosyaSayisi: dagitilmisDosyalar.filter((f) => f.atananHakim === judge.name).length,
        toplamPuan: yeniHakimPuanlari[judge.id] || judge.score,
        hedefPuan: hakimHedefPuanlari[judge.id] || 0,
      })),
      yeniHakimPuanlari,
      hakimHedefPuanlari,
      toplamDosyaPuani,
      ortalamaHakimPuani,
      toplamDosyaSayisi,
      dagitilmisDosyaSayisi,
    };

    const dosyaByName = getDosyaSayisiByJudgeNameFromDistribution({
      summaryReport: newSummary,
      distributedFiles: dagitilmisDosyalar,
    });
    const mergedJudges = judgesForNames.map((judge) => {
      const ekDosya = dosyaByName.get(judge.name) ?? 0;
      const curCount = Number(judge.dosyaSayisi) || 0;
      return {
        ...judge,
        score: yeniHakimPuanlari[judge.id] ?? judge.score,
        dosyaSayisi: curCount + (Number.isFinite(ekDosya) ? ekDosya : 0),
      };
    });

    return { updatedFiles, dagitilmisDosyalar, newSummary, mergedJudges };
  };

  const isAssignedToFormerJudge = (file) => {
    if (!file.atananHakim) return false;
    return matchFormerJudgeAssignment(file, file.atananHakim);
  };

  const isAssignedToManualJudge = (file) => {
    if (!file.manuelTevziEdilecekHakim || !file.atananHakim) return false;
    
    const normalizedManuelHakim = turkishToLower(file.manuelTevziEdilecekHakim);
    const normalizedAtananHakim = turkishToLower(file.atananHakim);
    
    return normalizedManuelHakim.includes(normalizedAtananHakim) || normalizedAtananHakim.includes(normalizedManuelHakim);
  };

  const syncStateAfterManualMove = (nextDistributed) => {
    const baseline = judgeScoreBaselineBeforeDistributionRef.current || {};
    const dosyaBaseline = judgeDosyaSayisiBaselineBeforeDistributionRef.current || {};

    // Her hakim için taşıma sonrası yük / dosya sayısı ve puan
    const judgeScores = {};
    const judgeLoadSums = {};
    const judgeDosyaCounts = {};
    const judgeAssignedFiles = {};
    const judgeHamPuanToplam = {};
    const judgeEskiDosyalar = {};

    judges.forEach((judge) => {
      const assigned = nextDistributed.filter((f) => f.atananHakim === judge.name);
      const loadSum = assigned.reduce((sum, f) => {
        if (isAssignedToFormerJudge(f)) {
          return sum + f.dosyaPuani * calculationSettings.hakimKendiDosyasiCarpani;
        }
        return sum + f.dosyaPuani;
      }, 0);
      const hamToplam = assigned.reduce((s, f) => s + (f.dosyaPuani || 0), 0);
      const eskiler = assigned.filter((f) => isAssignedToFormerJudge(f));

      const b = getBaselineEntry(baseline, judge.id);
      judgeScores[judge.id] = b + loadSum;
      judgeLoadSums[judge.id] = loadSum;
      judgeDosyaCounts[judge.id] = assigned.length;
      judgeAssignedFiles[judge.id] = assigned;
      judgeHamPuanToplam[judge.id] = hamToplam;
      judgeEskiDosyalar[judge.id] = eskiler;
    });

    const updatedFiles = files.map((f) => {
      const dist = nextDistributed.find((d) => d.id === f.id);
      if (!dist) return f;
      return {
        ...f,
        atananHakim: dist.atananHakim,
        alinanPuan: calculateAlinanPuan(f, dist.atananHakim, calculationSettings),
      };
    });

    const updatedJudges = judges.map((j) => {
      const db = getBaselineEntry(dosyaBaseline, j.id);
      return {
        ...j,
        score: judgeScores[j.id] ?? j.score,
        dosyaSayisi: db + (judgeDosyaCounts[j.id] ?? 0),
      };
    });

    const hedefMap =
      distributionResults?.hakimHedefPuanlari ?? summaryReport?.hakimHedefPuanlari;

    const rebuiltSummary = summaryReport
      ? {
          ...summaryReport,
          yeniHakimPuanlari: judgeScores,
          hakimRaporlari: judges.map((judge) => {
            const eskiler = judgeEskiDosyalar[judge.id] || [];
            return {
              hakimAdi: judge.name,
              dosyaSayisi: judgeDosyaCounts[judge.id] ?? 0,
              toplamPuan: judgeScores[judge.id],
              atananDosyalarToplamPuani: judgeHamPuanToplam[judge.id] ?? 0,
              eskiDosyaSayisi: eskiler.length,
              eskiDosyaPuani: eskiler.reduce((s, f) => s + (f.dosyaPuani || 0), 0),
              hedefPuan: hedefMap?.[judge.id] ?? hedefMap?.[String(judge.id)] ?? 0,
            };
          }),
          toplamDosyaSayisi: nextDistributed.length,
          dagitilmisDosyaSayisi: nextDistributed.length,
          toplamAtananDosyaPuani: judges.reduce(
            (s, j) => s + (judgeHamPuanToplam[j.id] || 0),
            0
          ),
        }
      : null;

    setJudges(updatedJudges);
    setFiles(updatedFiles);
    if (rebuiltSummary) setSummaryReport(rebuiltSummary);

    setDistributionResults((prev) => {
      if (!prev) return prev;
      const hakimDosyaSayilari = {};
      judges.forEach((j) => {
        hakimDosyaSayilari[j.id] = judgeDosyaCounts[j.id] ?? 0;
      });
      const dengesizlikOrani = recalcDengesizlikOrani(
        judgeScores,
        prev.hakimHedefPuanlari,
        judges
      );
      return {
        ...prev,
        yeniHakimPuanlari: judgeScores,
        hakimDosyaSayilari,
        ...(dengesizlikOrani != null ? { dengesizlikOrani } : {}),
      };
    });

    // Kayıtlı bir tevzi açıkken yapılan manuel taşımayı kayda ve (uygulanmışsa)
    // genel hakim listesine anında yansıt. Aksi halde ekrandaki state güncellenir,
    // kullanıcı "Sonuçları Kaydet" ile kaydedene kadar kalıcılaşmaz.
    const loaded = loadedDistributionRef.current;
    if (!loaded) return;

    setFolders((prevFolders) => {
      const folder = prevFolders.find((f) => f.id === loaded.folderId);
      const existing = folder?.distributions?.[loaded.distributionIndex];
      if (!existing) return prevFolders;

      const judgeScoreDeltaFromSave = Object.fromEntries(
        judges.map((j) => [String(j.id), Number(judgeLoadSums[j.id]) || 0])
      );
      const judgeDosyaSayisiDeltaFromSave = Object.fromEntries(
        judges.map((j) => [String(j.id), Number(judgeDosyaCounts[j.id]) || 0])
      );

      const updatedDistribution = {
        ...existing,
        files: updatedFiles,
        distributedFiles: nextDistributed,
        summaryReport: rebuiltSummary || existing.summaryReport,
        judgeScoreDeltaFromSave,
        judgeDosyaSayisiDeltaFromSave,
      };

      const nextFolders = upsertDistributionInFolders(
        prevFolders,
        loaded.folderId,
        loaded.distributionIndex,
        updatedDistribution
      );
      localStorage.setItem('distributionFolders', JSON.stringify(nextFolders));
      return nextFolders;
    });

    if (loaded.applied) {
      localStorage.setItem('judges', JSON.stringify(updatedJudges));
      window.dispatchEvent(new Event('judges-localstorage-updated'));
    }
  };

  const handleReassignFileToJudge = (fileId, newJudgeName) => {
    if (!newJudgeName) return;
    const target = judges.find((j) => j.name === newJudgeName);
    if (!target) return;

    let moved = false;
    let nextDistributed = null;

    flushSync(() => {
      setDistributedFiles((prevDistributed) => {
        const file = prevDistributed.find((f) => f.id === fileId);
        if (!file || file.atananHakim === newJudgeName) {
          return prevDistributed;
        }
        moved = true;
        // Savunma: aynı id'li birden fazla dosya bulunsa (örn. birleşik moddaki
        // tevziler arası eski id çakışmaları), referans eşitliğiyle sadece bir tanesi
        // taşınır → hedef hakime çift dosya eklenmez.
        nextDistributed = prevDistributed.map((f) =>
          f === file ? { ...f, atananHakim: newJudgeName } : f
        );
        return nextDistributed;
      });
    });

    if (moved && nextDistributed) {
      syncStateAfterManualMove(nextDistributed);
    }
  };

  const handleSaveJudgeFromModal = (updatedJudge) => {
    const prev = judges.find((j) => j.id === updatedJudge.id);
    if (!prev) {
      setJudgeModalJudge(null);
      return;
    }
    const updatedJudges = judges.map((j) => (j.id === updatedJudge.id ? updatedJudge : j));
    commitJudges(updatedJudges);

    if (prev.name !== updatedJudge.name) {
      setDistributedFiles((df) =>
        df.map((f) => (f.atananHakim === prev.name ? { ...f, atananHakim: updatedJudge.name } : f))
      );
      setFiles((fl) =>
        fl.map((f) => (f.atananHakim === prev.name ? { ...f, atananHakim: updatedJudge.name } : f))
      );
      setSummaryReport((sr) => {
        if (!sr?.hakimRaporlari) return sr;
        return {
          ...sr,
          hakimRaporlari: sr.hakimRaporlari.map((h) =>
            h.hakimAdi === prev.name ? { ...h, hakimAdi: updatedJudge.name } : h
          ),
        };
      });
    }

    setJudgeModalJudge(null);
  };

  const handleFileClick = (file) => {
    setSelectedFile(file);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedFile(null);
  };
  const generateSummaryReport = () => {
    const hakimRaporlari = judges.map(hakim => {
      const atananDosyalar = distributedFiles.filter(dosya => dosya.atananHakim === hakim.name);
      const atananDosyalarToplamPuani = atananDosyalar.reduce((total, dosya) => total + (dosya.dosyaPuani || 0), 0);
      const eskiDosyalar = atananDosyalar.filter(dosya => isAssignedToFormerJudge(dosya));
      const eskiDosyaPuani = eskiDosyalar.reduce((total, dosya) => total + (dosya.dosyaPuani || 0), 0);

      return {
        hakimAdi: hakim.name,
        dosyaSayisi: atananDosyalar.length,
        atananDosyalarToplamPuani,
        eskiDosyaSayisi: eskiDosyalar.length,
        eskiDosyaPuani
      };
    });

    const toplamDosyaSayisi = distributedFiles.length;
    const toplamAtananDosyaPuani = hakimRaporlari.reduce((total, hakim) => total + hakim.atananDosyalarToplamPuani, 0);

    return {
      hakimRaporlari,
      toplamDosyaSayisi,
      toplamAtananDosyaPuani
    };
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-6 text-center text-indigo-700">Dosya Yönetimi</h1>
      
      <div className="mb-8 bg-gray-50 shadow-md rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Dosya Yükleme ve İşlemler</h3>
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <FileUploader
            onFileUpload={handleFileUpload}
            uploadProgress={uploadProgress}
            uploadedFileName={uploadedFileName}
          />
          {isFileUploaded && (
            <ActionButtons
              onDistribute={handleDistribute}
              distributionStatus={distributionStatus}
            />
          )}
        </div>
        <DragDropArea
          isDragging={isDragging}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        />
      </div>

      {isFileUploaded && (
        <div className="mb-6 bg-gray-100 shadow-md rounded-lg overflow-hidden">
          <div className="flex justify-between items-center border-b p-4">
            <TabNavigation
              activeTab={activeTab}
              onTabChange={setActiveTab}
              isDistributionCompleted={isDistributionCompleted}
            />
            {isDistributionCompleted && (
              <NavigationButtons
                onExportToExcel={exportToExcel}
                onExportToWord={exportToWord}
                onSaveResults={handleSaveResults}
                onSaveAsNewFolder={allResultsClusterRef.current ? handleSaveAsNewFolder : null}
              />
            )}
          </div>

          <div className="p-4">
            {activeTab === 'mainTable' && (
              <MainTable
                files={files}
                isAssignedToFormerJudge={isAssignedToFormerJudge}
                isAssignedToManualJudge={isAssignedToManualJudge}
                onFileClick={handleFileClick}
                onJudgeClick={handleJudgeClickFromMainTable}
              />
            )}

            {isDistributionCompleted && activeTab === 'summary' && (
              <SummaryReport summaryReport={generateSummaryReport()} />
            )}

            {isDistributionCompleted && activeTab === 'judgeResults' && summaryReport && judges && judges.length > 0 && (
              <DistributionResults 
                hakimler={judges} 
                dagitimSonuclari={{
                  ...summaryReport,
                  distributedFiles: distributedFiles,
                  yeniHakimPuanlari: summaryReport.yeniHakimPuanlari,
                  hakimDetaylari: judges.map(judge => {
                    const judgeDosyalar = distributedFiles.filter(file => file.atananHakim === judge.name);
                    const eskiDosyalar = judgeDosyalar.filter(dosya => isAssignedToFormerJudge(dosya));
                    const yeniDosyalar = judgeDosyalar.filter(dosya => !isAssignedToFormerJudge(dosya));
                    return {
                      hakimAdi: judge.name,
                      toplamPuan: judgeDosyalar.reduce((sum, dosya) => sum + dosya.dosyaPuani, 0),
                      dosyaSayisi: judgeDosyalar.length,
                      eskiDosyaSayisi: eskiDosyalar.length,
                      eskiDosyaPuani: eskiDosyalar.reduce((sum, dosya) => sum + dosya.dosyaPuani, 0),
                      yeniDosyaSayisi: yeniDosyalar.length,
                      yeniDosyaPuani: yeniDosyalar.reduce((sum, dosya) => sum + dosya.dosyaPuani, 0),
                    };
                  })
                }} 
              />
            )}

            {isDistributionCompleted && activeTab === 'judgeDetails' && distributedFiles.length > 0 && (
              <JudgeDetails
                distributedFiles={distributedFiles}
                judges={judges}
                calculationSettings={calculationSettings}
                distributionResults={distributionResults}
                summaryReport={summaryReport}
                dosyaSayisiTevziOncesiByJudgeId={judgeDosyaSayisiBaselineBeforeDistributionRef.current}
                judgeDetailsSortOrder={judgeDetailsSortOrder}
                setJudgeDetailsSortOrder={(order) => {
                  setJudgeDetailsSortOrder(order);
                  setJudgeDetailsCardOrder(null);
                }}
                judgeDetailsCardOrder={judgeDetailsCardOrder}
                setJudgeDetailsCardOrder={setJudgeDetailsCardOrder}
                handleFileClick={handleFileClick}
                isAssignedToFormerJudge={isAssignedToFormerJudge}
                isAssignedToManualJudge={isAssignedToManualJudge}
                onReassignFileToJudge={handleReassignFileToJudge}
                onEditJudge={(judge) => setJudgeModalJudge(judge)}
                scrollToJudgeId={scrollToJudgeId}
                onScrollToJudgeHandled={() => setScrollToJudgeId(null)}
              />
            )}
          </div>
        </div>
      )}

      {!isFileUploaded && (
        <div className="text-center text-gray-600 mt-4">
          Lütfen işlem yapmak için bir Excel veya ODS dosyası yükleyin.
        </div>
      )}

      <FileDetailModal
        file={selectedFile}
        isOpen={isModalOpen}
        onClose={closeModal}
        sourceFileName={selectedFile?.sourceFileName || uploadedFileName}
      />

      {judgeModalJudge && (
        <JudgeEditModal
          judge={judgeModalJudge}
          onClose={() => setJudgeModalJudge(null)}
          onSave={handleSaveJudgeFromModal}
        />
      )}

      {overwriteModal && (
        <OverwriteModal
          uploadedFileName={uploadedFileName}
          onCancel={() => setOverwriteModal(null)}
          onSaveRecordOnly={handleOverwriteSaveRecordOnly}
          onFullRedistribute={handleOverwriteFullRedistribute}
        />
      )}
    </div>
  );
};

export { FileManagement };